import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, ArrowLeft, Loader2, KeyRound, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ASSETS } from "@/lib/assets";

/**
 * AdminPortal — Lien de connexion réservé au personnel de l'école.
 * URL : /admin-portal (volontairement discrète, non listée dans la nav publique)
 * Tous les rôles staff y ont accès (voir STAFF_ROLES).
 */
const STAFF_ROLES = ["super_admin", "admin", "founder", "finance", "teacher", "scolarite", "director", "nurse", "canteen", "staff"];

export default function AdminPortal() {
  const navigate = useNavigate();
  const { login, verifyOtp } = useAuth();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState("");
  const [emailMasked, setEmailMasked] = useState("");
  const [otpChannel, setOtpChannel] = useState("email");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    if (step === "otp" && codeRef.current) codeRef.current.focus();
  }, [step]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) return toast.error("Email/téléphone et mot de passe requis");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.require_otp) {
        setPendingId(res.pending_id);
        setEmailMasked(res.destination_masked || res.email_masked || email);
        setOtpChannel(res.channel || "email");
        setDevOtp(res.dev_otp || "");
        setStep("otp");
        if (res.dev_otp) toast.info(`Mode test — code : ${res.dev_otp}`);
        else toast.success(res.channel === "sms" ? "Code envoyé par SMS." : "Code envoyé par email.");
      } else {
        // Vérifie que c'est bien un compte staff
        if (!STAFF_ROLES.includes(res.user.role)) {
          toast.error("Ce portail est réservé au personnel de l'école.");
          return;
        }
        toast.success("Bienvenue dans l'espace personnel.");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!code || code.length !== 6) return toast.error("Code à 6 chiffres requis");
    setLoading(true);
    try {
      const user = await verifyOtp(pendingId, code);
      if (!STAFF_ROLES.includes(user.role)) {
        toast.error("Ce portail est réservé au personnel de l'école.");
        return;
      }
      toast.success("Connexion validée.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4 relative overflow-hidden" data-testid="page-admin-portal">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.07] bg-dot-pattern" />
      <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#1E3A8A]/30 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#EA580C]/20 blur-3xl" />

      <div className="relative w-full max-w-md">
        <button onClick={() => navigate("/")} className="text-xs text-slate-400 hover:text-white mb-5 flex items-center gap-1" data-testid="admin-back-home">
          <ArrowLeft className="h-3 w-3" /> Retour au site
        </button>

        <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] p-6 text-center border-b border-white/10">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-4 ring-1 ring-white/20">
              <img src={ASSETS.logo} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
            </div>
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EA580C]/20 border border-[#EA580C]/40 mb-2">
              <Lock className="h-3 w-3 text-[#FBBF24]" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FBBF24]">Accès réservé</span>
            </div>
            <h1 className="font-heading font-extrabold text-2xl text-white mb-1">Portail personnel</h1>
            <p className="text-xs text-slate-300">LES ÉCOLES MAZID · Espace sécurisé</p>
          </div>

          <div className="p-6">
            {step === "credentials" && (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="admin-email" className="text-slate-300">Email professionnel ou téléphone</Label>
                  <Input
                    id="admin-email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@ecoles-mazid.gn ou 6XX XX XX XX"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="admin-portal-email"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-pass" className="text-slate-300">Mot de passe</Label>
                  <PasswordInput
                    id="admin-pass"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="•••••••••••"
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                    data-testid="admin-portal-password"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#EA580C] hover:bg-orange-700 text-white gap-2 py-6 font-bold" data-testid="admin-portal-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4" /> Demander le code</>}
                </Button>
                <p className="text-[11px] text-slate-500 text-center pt-2">
                  Un code de vérification à 6 chiffres vous sera envoyé par SMS ou email selon votre préférence.
                </p>
              </form>
            )}

            {step === "otp" && (
              <form onSubmit={submitOtp} className="space-y-4">
                <button type="button" onClick={() => { setStep("credentials"); setCode(""); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="admin-otp-back">
                  <ArrowLeft className="h-3 w-3" /> Retour
                </button>

                <div className="text-center py-2">
                  <ShieldCheck className="h-10 w-10 text-[#FBBF24] mx-auto mb-2" />
                  <div className="text-sm text-white font-semibold mb-1">Vérification de sécurité</div>
                  <div className="text-xs text-slate-400">Code envoyé par {otpChannel === "sms" ? "SMS" : "email"} à <strong className="text-slate-200">{emailMasked}</strong></div>
                </div>

                {devOtp && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex items-start gap-2" data-testid="admin-dev-otp">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>Mode test — votre code : <code className="font-mono font-bold text-amber-100 text-sm">{devOtp}</code></div>
                  </div>
                )}

                <Input
                  ref={codeRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="• • • • • •"
                  className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] font-bold h-14"
                  data-testid="admin-otp-input"
                />

                <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-[#EA580C] hover:bg-orange-700 text-white gap-2 py-6 font-bold disabled:opacity-40" data-testid="admin-otp-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4" /> Accéder au tableau de bord</>}
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-5">
          Cette zone est strictement réservée. Toute tentative d&apos;accès non autorisé est enregistrée.
        </p>
      </div>
    </div>
  );
}
